import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Me, Session } from '@popcart/core/v1';
import { api, errorStatus, sessionToken, setUnauthorizedHandler } from '../api';

type Status = 'loading' | 'signedOut' | 'signedIn';

interface AuthValue {
  status: Status;
  me: Me | null;
  signIn(session: Session): void;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<Status>(sessionToken.get() ? 'loading' : 'signedOut');

  const dropSession = useCallback(() => {
    sessionToken.clear();
    setMe(null);
    setStatus('signedOut');
    qc.clear();
  }, [qc]);

  useEffect(() => {
    setUnauthorizedHandler(dropSession);
    return () => setUnauthorizedHandler(null);
  }, [dropSession]);

  // Restore a session kept in this tab.
  useEffect(() => {
    if (!sessionToken.get()) return;
    let live = true;
    api
      .me()
      .then((m) => {
        if (!live) return;
        setMe(m);
        setStatus('signedIn');
      })
      .catch((err: unknown) => {
        if (!live) return;
        // 401 already dropped the session; anything else (offline) also lands on sign-in.
        if (errorStatus(err) !== 401) sessionToken.clear();
        setStatus('signedOut');
      });
    return () => {
      live = false;
    };
  }, []);

  const signIn = useCallback((s: Session) => {
    sessionToken.set(s.token);
    setMe(s.user);
    setStatus('signedIn');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } catch {
      /* the server may already have forgotten it */
    }
    dropSession();
  }, [dropSession]);

  const value = useMemo(() => ({ status, me, signIn, signOut }), [status, me, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The auth value, or null outside the provider (for helpers that also run on their own). */
export function useMaybeAuth(): AuthValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
